import * as moment from 'moment';
import { isStoreLineItemEditable, StoresEligibleForSplit } from '~/types/Store';
import { isNotNil } from '~/utils/utils';
import Address, * as address from './Address';
import APIAddress from './APIAddress';
import * as APIError from './APIError';
import { requiresCustomsDeclaration as isCustomsRequired } from './CustomsDeclaration';
import LineItem, * as lineItem from './LineItem';
import {
  BatchEntryExtended,
  batchEntryExtendedFromJSON,
  JSONBatchEntryExtended,
  WebShipment,
} from './OrderBatchEntry';
import { BatchEntryId, BatchEntryStatus } from './OrdersBatch';
import Parcel, * as parcel from './Parcel';
import * as rates from './Rate';
import Transaction, * as transactions from './Transaction';

interface BaseJSONOrder {
  object_id: string;
  order_number: string;
  order_status: string;
  errors: APIError.JSONAPIError[];
  validation_status: string;
  placed_at: string;
  created_at?: string; // v1 API does not have placed_at
  object_owner: string;
  to_address: address.JSONAddress;
  address_from: address.JSONAddress;
  address_return: address.JSONAddress;
  shop_app: string;
  weight: Maybe<string>;
  weight_unit: Maybe<string>;
  // TODO: Revisit
  transactions: transactions.JSONTransaction[];
  rates: rates.JSONBaseRate[];
  total_tax: string;
  total_price: Maybe<string>;
  subtotal_price: Maybe<string>;
  currency: Maybe<string>;
  shipping_method: string;
  shipping_cost: string;
  shipping_cost_currency: string;
  line_items: lineItem.JSONLineItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes: Maybe<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  hidden: boolean;
  partial_fulfillment_requests?: JSONBatchEntryExtended[];
}

interface JSONOrder extends BaseJSONOrder {
  parcel?: parcel.JSONParcel;
}

interface SavedJSONOrder extends BaseJSONOrder {
  parcel?: parcel.SavedJSONParcel;
}

export type OrderTagIconType =
  | 'heart'
  | 'diamond'
  | 'square'
  | 'star'
  | 'flower'
  | 'circle'
  | 'tree'
  | 'sprout'
  | 'hourglass'
  | 'puzzle'
  | 'flag'
  | 'person'
  | 'default';

export type OrderTag = {
  color?: string;
  icon: OrderTagIconType;
  id: string;
  name: string;
};

export type OrderTagsMapping = {
  [key: string]: OrderTag[];
};

type OrderObjectId = string;

type Order = {
  objectId: OrderObjectId;
  orderNumber: string;
  orderStatus: OrderStatus;
  errors: APIError.APIError[];
  validationStatus: Maybe<OrderValidationStatus>;
  placedAt: moment.Moment;
  objectOwner: string;
  toAddress: Maybe<Address>;
  addressFrom?: Maybe<Address>;
  addressReturn?: Maybe<Address>;
  parcel?: Maybe<Parcel>;
  shopApp: string;
  weight: Maybe<string>;
  weightUnit: Maybe<string>;
  // TODO: Revisit
  transactions: Transaction[];
  rates: rates.BaseRate[];
  totalTax: string;
  totalPrice: Maybe<string>;
  subtotalPrice: Maybe<string>;
  currency: Maybe<string>;
  shippingMethod: string;
  shippingCost: string;
  shippingCostCurrency: string;
  lineItems: LineItem[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  notes: Maybe<any>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  items: any[];
  hidden: boolean;
  partialFulfillmentRequests: BatchEntryExtended[];
  totalPartialFulfillments: number;
  extras?: {
    alcohol: boolean;
    dryIce: boolean;
    insurance: boolean;
    isBypassAddressValidation: boolean;
    isBypassCustoms: boolean;
    isResidential: boolean;
    isReturnRequested: boolean;
    packingSlipText: string;
    signatureConfirmation: boolean;
  };
  tags?: OrderTag[];
};

export type WebOrder = {
  created_at: string;
  currency: string;
  notes: string;
  order_number: string;
  order_status: OrderStatus;
  shipping_cost: string;
  shipping_cost_currency: string;
  shipping_method: string;
  shop_app: string;
  subtotal_price: string;
  total_price: string;
  total_tax: string;
  weight: string;
  weight_unit: string;
  copied_from_order_object_id: string;
  hidden: boolean;
  object_id: string;
  object_owner: string;
  validation_status: Maybe<OrderValidationStatus>;
  line_items: lineItem.WebLineItem[];
  shipments: WebShipment[];
  transactions: transactions.WebTransaction[];
  errors: APIError.APIError[];
  tags?: OrderTag[];
};

enum OrderStatus {
  AWAITPAY = 'AWAITPAY',
  VALID = 'VALID',
  INVALID = 'INVALID',
  PAID = 'PAID',
  PARTIALLY_FULFILLED = 'PARTIALLY_FULFILLED',
  SHIPPED = 'SHIPPED',
  REFUNDED = 'REFUNDED',
  CANCELLED = 'CANCELLED',
  UNKNOWN = 'UNKNOWN',
  GENERIC = 'GENERIC',
}

enum OrderValidationStatus {
  VALID = 'VALID',
  INVALID = 'INVALID',
}

enum OrderState {
  CANCELLED = 'CANCELLED',
  REFUNDED = 'REFUNDED',
  REFUND_REJECTED = 'REFUND_REJECTED',
  REFUND_PENDING = 'REFUND_PENDING',
  FULFILLED_OUTSIDE_SHIPPO = 'FULFILLED_OUTSIDE_SHIPPO',
  FULFILLED_SINGLE_LABEL = 'FULFILLED_SINGLE_LABEL',
  FULFILLED_MULTIPLE_LABELS = 'FULFILLED_MULTIPLE_LABELS',
  PARTIALLY_FULFILLED_SINGLE_LABEL = 'PARTIALLY_FULFILLED_SINGLE_LABEL',
  PARTIALLY_FULFILLED_SINGLE_REFUND = 'PARTIALLY_FULFILLED_SINGLE_REFUND', // denotes that refund action has been taken
  PARTIALLY_FULFILLED_MULTIPLE_LABELS = 'PARTIALLY_FULFILLED_MULTIPLE_LABELS',
  NOT_FULFILLED = 'NOT_FULFILLED',
}

export enum SyncStatus {
  COMPLETE = 'COMPLETE',
  IN_PROGRESS = 'IN_PROGRESS',
  FAILED = 'FAILED',
}

export type DuplicatedOrderStatus = {
  order_url: string;
  source_order_id: string;
  cloned_order_id: string;
  sync_status: SyncStatus;
};

// Types to render orders in the table
type PartialChild = {
  kind: 'partial-child';
  order: Order;
  batchEntry: BatchEntryExtended;
};

type PartialParent = {
  kind: 'partial-parent';
  order: Order;
  batchEntry: Maybe<BatchEntryExtended>;
};

type Single = {
  kind: 'single';
  order: Order;
  batchEntry: Maybe<BatchEntryExtended>;
};

export type DropdownOption = {
  text: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onClick?: (evt: any) => void;
  href?: string;
  tagName: string;
};

export type OrdersRenderData = Single | PartialParent | PartialChild;

export type OrderSelection = HashMap<OrderObjectId, BatchEntryId[]>;

// From the order selection, get an object that contains the most specific IDs possible.
// If the order has batch entries, we get its batch entry ideas; otherwise, we get its order object id.
export function actionableOrderSelectionIds(orderSelection: OrderSelection): {
  orderObjectIds: string[];
  batchEntryIds: number[];
} {
  return Object.entries(orderSelection).reduce<{
    orderObjectIds: string[];
    batchEntryIds: number[];
  }>(
    (obj, [orderObjectId, batchEntryIds]) => {
      if (batchEntryIds.length > 0) {
        return { ...obj, batchEntryIds: [...obj.batchEntryIds, ...batchEntryIds] };
      }

      return { ...obj, orderObjectIds: [...obj.orderObjectIds, orderObjectId] };
    },
    { orderObjectIds: [], batchEntryIds: [] },
  );
}

export function orderSelectionOrderObjectIds(orderSelection: OrderSelection) {
  return Object.keys(orderSelection);
}

export function orderSelectionBatchEntryIds(orderSelection: OrderSelection): number[] {
  return Object.values(orderSelection).reduce((accum, beIds) => {
    return [...accum, ...beIds];
  }, []);
}

export interface SelectedOrdersAddresses {
  senderAddresses: address.BaseAddress[];
  returnAddresses: address.BaseAddress[];
}

function fromJSON(jsonOrder: JSONOrder): Order {
  return {
    objectId: jsonOrder.object_id,
    orderNumber: jsonOrder.order_number,
    orderStatus: <OrderStatus>jsonOrder.order_status,
    errors: jsonOrder.errors.map(APIError.fromJSON),
    validationStatus: <OrderValidationStatus>jsonOrder.validation_status,
    placedAt: moment(jsonOrder.placed_at || jsonOrder.created_at), // V1 uses created_at instead of placed_at
    objectOwner: jsonOrder.object_owner,
    toAddress: address.fromJSON(jsonOrder.to_address),
    addressFrom: address.fromJSON(jsonOrder.address_from),
    addressReturn: address.fromJSON(jsonOrder.address_return),
    parcel: jsonOrder.parcel ? parcel.fromJSON(jsonOrder.parcel) : null,
    shopApp: jsonOrder.shop_app,
    weight: jsonOrder.weight,
    weightUnit: jsonOrder.weight_unit,
    transactions: jsonOrder.transactions.map(transactions.fromJSON),
    rates: jsonOrder.rates ? jsonOrder.rates.map(rates.fromJSONBaseRate) : [],
    totalTax: jsonOrder.total_tax,
    totalPrice: jsonOrder.total_price,
    subtotalPrice: jsonOrder.subtotal_price,
    currency: jsonOrder.currency,
    shippingMethod: jsonOrder.shipping_method,
    shippingCost: jsonOrder.shipping_cost,
    shippingCostCurrency: jsonOrder.shipping_cost_currency,
    lineItems: lineItem.sort(
      jsonOrder.line_items.filter((lineItem) => lineItem.quantity > 0).map(lineItem.fromJSON),
    ),
    notes: jsonOrder.notes,
    items: jsonOrder.items,
    hidden: jsonOrder.hidden,
    partialFulfillmentRequests: jsonOrder.partial_fulfillment_requests
      ? jsonOrder.partial_fulfillment_requests.map((x) => ({
          ...batchEntryExtendedFromJSON(x),
        }))
      : [],
    totalPartialFulfillments: jsonOrder.partial_fulfillment_requests
      ? jsonOrder.partial_fulfillment_requests.length
      : 0,
    tags: undefined,
  };
}

function toJSON(order: Order): Partial<SavedJSONOrder> {
  return {
    object_id: order.objectId,
    order_number: order.orderNumber,
    order_status: order.orderStatus,
    errors: order.errors,
    validation_status: order.validationStatus || '',
    placed_at: order.placedAt.toISOString(),
    object_owner: order.objectOwner,
    // Casting partial back to address, it is fine if some fields are missing.
    to_address: order.toAddress ? address.toJSON(<Address>order.toAddress) : undefined,
    address_from: order.addressFrom ? address.toJSON(order.addressFrom) : undefined,
    address_return: order.addressReturn ? address.toJSON(order.addressReturn) : undefined,
    parcel: order.parcel ? parcel.toJSON(order.parcel) : undefined,
    shop_app: order.shopApp,
    weight: order.weight,
    weight_unit: order.weightUnit,
    total_tax: order.totalTax,
    total_price: order.totalPrice,
    subtotal_price: order.subtotalPrice,
    currency: order.currency,
    shipping_method: order.shippingMethod,
    shipping_cost: order.shippingCost,
    shipping_cost_currency: order.shippingCostCurrency,
    notes: order.notes,
    hidden: order.hidden,
  };
}

export function fromWebOrder(webOrder: WebOrder): Order {
  const txs = webOrder.transactions.map((t) => transactions.fromWebTransaction(t));
  const litems = webOrder.line_items.map((item) => lineItem.fromWebLineItem(item));
  const orderRates = webOrder.transactions.map((t) => rates.fromWebRate(t.rate));
  const firstShipment = webOrder.shipments.length > 0 ? webOrder.shipments[0] : undefined;
  const firstParcel =
    firstShipment && firstShipment?.parcels?.length > 0 ? firstShipment?.parcels[0] : undefined;

  return {
    objectId: webOrder.object_id,
    orderNumber: webOrder.order_number,
    orderStatus: <OrderStatus>webOrder.order_status,
    errors: webOrder.errors,
    validationStatus: <OrderValidationStatus>webOrder.validation_status,
    placedAt: moment(webOrder.created_at), //Missing placedAt
    objectOwner: webOrder.object_owner,
    shopApp: webOrder.shop_app,
    weight: webOrder.weight,
    weightUnit: webOrder.weight_unit,
    transactions: txs,
    totalTax: webOrder.total_tax,
    totalPrice: webOrder.total_price,
    subtotalPrice: webOrder.subtotal_price,
    currency: webOrder.currency,
    shippingMethod: webOrder.shipping_method,
    shippingCost: webOrder.shipping_cost,
    shippingCostCurrency: webOrder.shipping_cost_currency,
    lineItems: litems,
    items: litems,
    notes: webOrder.notes,
    hidden: webOrder.hidden,
    rates: orderRates,
    toAddress: firstShipment?.address_to ? address.fromWebAddress(firstShipment?.address_to) : null,
    addressFrom: firstShipment?.address_from
      ? address.fromWebAddress(firstShipment?.address_from)
      : null,
    addressReturn: firstShipment?.address_return
      ? address.fromWebAddress(firstShipment?.address_return)
      : null,
    parcel: firstParcel ? parcel.fromWebParcel(firstParcel) : null,
    tags: webOrder.tags,

    //nulls ?
    partialFulfillmentRequests: [],
    totalPartialFulfillments: 0,
  };
}

export function getOrderWeight(order: Order): Maybe<number> {
  return (order.weight && parseFloat(order.weight)) || null;
}

export function orderHasWeight(order: Order): boolean {
  const orderWeight = order.weight && parseFloat(order.weight);

  return (
    (orderWeight && !isNaN(orderWeight) && orderWeight > 0 && !!order.weightUnit) ||
    (order.lineItems.length > 0 && order.lineItems.every(lineItem.lineItemHasWeight))
  );
}

export function orderHasAmount(order: Order): boolean {
  return (
    order.currency !== null &&
    ((isNotNil(order.totalPrice) && parseFloat(order.totalPrice) !== 0) ||
      (isNotNil(order.subtotalPrice) && parseFloat(order.subtotalPrice) !== 0))
  );
}

export function isPaid(order: Order): boolean {
  return ![
    OrderStatus.AWAITPAY,
    OrderStatus.GENERIC,
    OrderStatus.REFUNDED,
    OrderStatus.CANCELLED,
  ].includes(order.orderStatus);
}

export function isCancelled(order: Order): boolean {
  return order.orderStatus === OrderStatus.CANCELLED;
}

export function isFulfilled(order: { orderStatus: OrderStatus }): boolean {
  return order.orderStatus === OrderStatus.SHIPPED;
}

export function isFulfilledSingleLabel(order: Order): boolean {
  return getOrderState(order) === OrderState.FULFILLED_SINGLE_LABEL;
}

export function isPartiallyFulfilled(order: Order): boolean {
  return order.orderStatus === OrderStatus.PARTIALLY_FULFILLED;
}

export function isRefundPending(order: Order): boolean {
  const successfulLiveTransactions = order.transactions.filter((txn) => {
    return !txn.test && txn.status !== transactions.TransactionStatus.ERROR;
  });
  if (successfulLiveTransactions.length === 0) {
    return false;
  }
  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDPENDING,
  );
}

export function isRefundRejected(order: Order): boolean {
  const successfulLiveTransactions = order.transactions.filter((txn) => {
    return !txn.test && txn.status !== transactions.TransactionStatus.ERROR;
  });
  if (successfulLiveTransactions.length === 0) {
    return false;
  }
  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDREJECTED,
  );
}

export function isRefunded(order: Order): boolean {
  const successfulLiveTransactions = order.transactions.filter((txn) => {
    return !txn.test && txn.status !== transactions.TransactionStatus.ERROR;
  });
  if (successfulLiveTransactions.length === 0) {
    return false;
  }
  return successfulLiveTransactions.every(
    (txn) => txn.status === transactions.TransactionStatus.REFUNDED,
  );
}

export function isRefundedOutsideShippo(order: Order): boolean {
  return order.orderStatus === OrderStatus.REFUNDED;
}

export function isValid(order: Order): boolean {
  return !order.validationStatus || order.validationStatus === OrderValidationStatus.VALID;
}

export function getOrderState(order: Order): OrderState {
  if (isPartiallyFulfilled(order)) {
    if (order.transactions.length === 0) return OrderState.FULFILLED_OUTSIDE_SHIPPO;
    if (order.transactions.length === 1) {
      if (isRefunded(order) || isRefundRejected(order) || isRefundPending(order)) {
        return OrderState.PARTIALLY_FULFILLED_SINGLE_REFUND;
      }
      return OrderState.PARTIALLY_FULFILLED_SINGLE_LABEL;
    }
    return OrderState.PARTIALLY_FULFILLED_MULTIPLE_LABELS;
  }

  if (isFulfilled(order) || isPartiallyFulfilled(order)) {
    if (order.transactions.length === 0) return OrderState.FULFILLED_OUTSIDE_SHIPPO;
    if (isRefunded(order)) return OrderState.REFUNDED;
    if (isRefundRejected(order)) return OrderState.REFUND_REJECTED;
    if (isRefundPending(order)) return OrderState.REFUND_PENDING;
    if (
      order.transactions.filter(
        (transaction) => transaction.status === transactions.TransactionStatus.SUCCESS,
      ).length === 1
    ) {
      return OrderState.FULFILLED_SINGLE_LABEL;
    }
    return OrderState.FULFILLED_MULTIPLE_LABELS;
  }

  if (isCancelled(order)) return OrderState.CANCELLED;
  if (isRefundedOutsideShippo(order)) return OrderState.REFUNDED;

  return OrderState.NOT_FULFILLED;
}

export function isFulfilledOutsideShippo(order: Order): boolean {
  return getOrderState(order) === OrderState.FULFILLED_OUTSIDE_SHIPPO;
}

// order is marked as fulfilled but does not have a parcel or a batch entry
export function isFulfilledInShippoOldExperience(order: Order): boolean {
  return isFulfilled(order) && !order.parcel;
}

export function getMaxShipTime(order: Order): Maybe<moment.Moment> {
  const shipTimes = order.lineItems.map((i) => i.maxShipTime).filter((v) => v);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return shipTimes && shipTimes.length > 0 ? moment(Math.min.apply(null, shipTimes as any)) : null;
}

export function getMaxDeliveryTime(order: Order): Maybe<moment.Moment> {
  const deliveryTimes = order.lineItems.map((i) => i.maxDeliveryTime).filter((v) => v);
  return deliveryTimes && deliveryTimes.length > 0
    ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
      moment(Math.min.apply(null, deliveryTimes as any))
    : null;
}

export function hasValidShippingInfo(order: Order): boolean {
  const parsedShippingCost = parseFloat(order.shippingCost);

  return (
    !!order.shippingCost &&
    order.shippingCost.toUpperCase() !== 'NONE' &&
    (parsedShippingCost > 0 || (parsedShippingCost === 0 && !!order.shippingMethod))
  );
}

export function hasValidTotalPrice(order: Order): boolean {
  return (
    !!order.totalPrice &&
    order.totalPrice.toUpperCase() !== 'NONE' &&
    parseFloat(order.totalPrice) > 0
  );
}

const manualOrderShopTypes: Partial<Record<string, boolean>> = {
  Shippo: true,
  CSV_Import: true,
};

export function isManualOrder(order: Order): boolean {
  return !!manualOrderShopTypes[order.shopApp];
}

// TODO: What is considered order that is ready to purchase
export function isReadyToPurchase(order: Order) {
  return !!order.toAddress && !!order.parcel && order.errors.length === 0;
}

export function requiresCustomsDeclaration(
  order: Order,
  defaultFromAddress: Maybe<APIAddress>,
): boolean {
  const addressFrom = (order && order.addressFrom) || defaultFromAddress; // guard against null order on hot reload
  const addressTo = order && order.toAddress;

  if (!addressTo || !addressFrom) return false;

  return isCustomsRequired(addressTo, addressFrom);
}

export function hasLineItems(order: Order): boolean {
  return order.lineItems.length > 0;
}

export function hasValidLineItems(order: Order) {
  const { lineItems } = order;
  return lineItems.length > 0 && lineItems.every(lineItem.isLineItemValid);
}

export function hasCustomsReadyLineItems(order: Order) {
  const { lineItems } = order;
  return !!lineItems.length && lineItems.every(lineItem.isCustomsReady);
}

export function hasRecipientAddressError(order: Order) {
  return (
    (!!order && !order.toAddress) ||
    (!!order?.toAddress && address.isAddressIncomplete(order.toAddress))
  );
}

/**
 * Order is eligible for splitting if the order is
 * not cancelled and the store is enabled for splitting, and
 * order has more than 1 quantity of a line item
 */
export const isEligibleForSplit = ({ orderStatus, shopApp, lineItems }: Order) =>
  orderStatus !== OrderStatus.CANCELLED &&
  // special logic for Magento since back-end returns "Magento_2" but feron expects "Magento 2"
  (StoresEligibleForSplit.has(shopApp) || shopApp === 'Magento_2') &&
  lineItems.reduce((currentTotal, item) => currentTotal + +item.quantity, 0) > 1;

export const hasPartialFulfillmentRequests = ({ partialFulfillmentRequests }: Order) => {
  return partialFulfillmentRequests && !!partialFulfillmentRequests.length;
};

export const isPartialFulfillmentAllCompleted = ({ partialFulfillmentRequests }: Order) => {
  return (
    partialFulfillmentRequests &&
    partialFulfillmentRequests.every((batchEntry) => {
      return batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION;
    })
  );
};

export const isOrderLineItemEditable = (order: Order): boolean => {
  return isStoreLineItemEditable(order.shopApp) && !isFulfilled(order);
};

export const newOrder = (
  fromAddress?: address.JSONBaseAddress,
  toAddress?: address.JSONBaseAddress,
) => {
  return {
    order_number: null,
    order_status: null,
    created_at: null,
    validation_status: null,
    shop_app: 'Shippo',
    uses_line_items: false,
    total_price: null,
    subtotal_price: null,
    total_tax: null,
    currency: null,
    shipping_cost: null,
    shipping_cost_currency: null,
    total_weight_unit: null,
    weight: null,
    weight_unit: null,
    shipping_method: null,
    notes: null,
    address_from: fromAddress || null,
    to_address: toAddress || null,
  };
};
// calculates the total value (amount or weight) for a given line item based on input unit value and quantity
export const calculateValueByQuantity = (value: string, quantity: number): string =>
  (Number(value) * quantity).toString();

export const sortOrderTagsByName = (tags: OrderTag[]): OrderTag[] => {
  if (tags.length === 0) return [];

  const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
  return tags.sort((previousTag, nextTag) => collator.compare(previousTag.name, nextTag.name));
};

export const fromWebOrderToOrderTagsMapping = (webOrders: WebOrder[]): OrderTagsMapping => {
  if (!webOrders) return {};

  return webOrders.reduce(
    (orderTags: OrderTagsMapping, { object_id, tags }) => ({
      ...orderTags,
      [object_id]: tags || [],
    }),
    {},
  );
};

export default Order;
export {
  fromJSON,
  OrderValidationStatus,
  OrderStatus,
  OrderState,
  toJSON,
  JSONOrder,
  OrderObjectId,
  SavedJSONOrder,
};
